// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// `robots.txt` and `sitemap.xml`, derived from the route table rather than typed beside it (M3-57).
//
// WHY THIS IS A MODULE AND NOT TWO FILES
// --------------------------------------
// Both files are lists of this product's own addresses, and this product already has exactly one
// list of those: `src/client/routes.ts`. A hand-written `sitemap.xml` is a second copy of it that
// nothing compares, so it drifts the first time a route is added — which is what happened to the
// sibling product, whose own build comment records the same conclusion: *"a hand-maintained
// public/sitemap.xml would drift out of sync with the indexable route set (and did)"*.
//
// So the route table is the source and this file decides one thing per route: **may a crawler
// index it, and if not, why not.** `INDEXING` has a key for every served route and a test fails
// when it does not, so a new route cannot be added without somebody answering the question. That
// is the difference between a sitemap that is generated and a sitemap that is merely written by a
// script.
//
// WHAT IS DELIBERATELY NOT IN THE SITEMAP
// ---------------------------------------
// Three kinds of address, and none of them is an oversight:
//
//   * **Pages that need an account.** `leaderboards.get` and `tournaments.current` are
//     `auth: 'user'` in `server/ts/routes.ts`, so a crawler — which is a signed-out visitor —
//     reaches a sign-in prompt. Offering a search engine a page that cannot answer is how a site
//     accumulates thin results that outrank its real ones.
//   * **Pages about one person.** `/account`, `/friends`, `/history`, `/profile/{handle}`,
//     `/replays/{id}`. A sitemap of handles is a directory of players nobody asked to publish.
//   * **Pages that are a mechanism rather than a destination.** `/headset` is a QR code for the
//     address you arrived from, `/link-headset` is a security prompt that `routes.ts` already
//     argues should never be one tap from idle browsing, and `/harness.html` is the physics
//     harness — a real second page of this build, and one no player is looking for.
//
// THE HONEST LIMIT
// ----------------
// This is a single-page application with no prerendering: every address in the sitemap is answered
// by the same `index.html` and the content arrives when React runs. Google renders JavaScript and
// will see the real page; a crawler that does not will see the shell at every URL. Listing only
// addresses that are *worth* rendering is what keeps that from being a duplicate-content problem,
// and prerendering — which is what the sibling product does — is the fix if it ever becomes one.

/**
 * The canonical origin.
 *
 * `render.yaml` attaches two hosts and `www.pinballgame.ca` answers `301 → https://pinballgame.ca/`,
 * so the apex is the only address that should ever appear in a sitemap, a canonical link or an
 * Open Graph URL. It is the same origin `PINBALL_SITE_URL` holds, which is where Stripe returns a
 * paying player — a second spelling here would give a subscription's return trip a different
 * origin from the one it left.
 */
export const SITE_ORIGIN = 'https://pinballgame.ca'

/**
 * How often the page changes, in the sitemap protocol's own vocabulary.
 *
 * Google ignores both this and `<priority>` and says so publicly. They are written anyway, because
 * the other crawlers that read a sitemap do not all ignore them and an omitted field cannot be
 * read by anybody.
 */
import { CHROME_LINKED_PATHS } from './chrome.ts'

export type ChangeFrequency = 'daily' | 'weekly' | 'monthly'

/** May a crawler index this route, and if not, why not. */
export type Indexing =
  | { readonly index: true; readonly changefreq: ChangeFrequency; readonly priority: string }
  | { readonly index: false; readonly because: string }

/**
 * The decision, for every route the router mounts.
 *
 * Keyed by the route table's own path, parameters and all, so `test/seo/sitemap.test.ts` can assert
 * that this record's keys are exactly `servedRoutes()`. A route added to the map without a decision
 * here fails that test by name, which is the only mechanism that reliably stops a new page from
 * being silently unlisted — or silently listed.
 */
export const INDEXING: Readonly<Record<string, Indexing>> = {
  // The front door: a machine, playable, with no account and no request (FR-030).
  '/': { index: true, changefreq: 'weekly', priority: '1.0' },
  // The roster. `machines.list` is `auth: 'public'` — a visitor choosing a machine has not signed
  // in yet — so this page answers a stranger, which is the whole test for belonging here.
  '/arcade': { index: true, changefreq: 'weekly', priority: '0.9' },
  // One machine, played. Expanded below over the first-party roster; a player-built machine is not
  // listed, because publishing somebody's machine to a search engine is a decision they have not
  // been asked to make.
  '/play/:machineId': { index: true, changefreq: 'weekly', priority: '0.8' },
  // One guide per machine (S3-10): the words a full-bleed cabinet cannot carry, at an address of
  // their own, written from the same definitions and expanded over the same roster.
  '/machines/:machineId': { index: true, changefreq: 'monthly', priority: '0.7' },
  // Anonymous by design (G3-09): "a price a visitor cannot read without first making an account is
  // an upsell this product's own arcade page argues against at length".
  '/pricing': { index: true, changefreq: 'monthly', priority: '0.7' },
  // 11 §5's workspace is "a place you may sit in without committing to anything", and
  // `BuilderRoute` creates nothing on open — so it renders for a signed-out visitor.
  '/builder': { index: true, changefreq: 'monthly', priority: '0.6' },
  // The six documents (S4). Every one is prose a stranger can read with no account, which is the
  // whole test for belonging here — and two of them are pages a *product* needs published rather
  // than pages that need traffic: a Store submission asks for a privacy-policy URL and a support
  // URL, and neither existed until these did.
  '/how-to-play': { index: true, changefreq: 'monthly', priority: '0.6' },
  '/support': { index: true, changefreq: 'monthly', priority: '0.5' },
  '/about': { index: true, changefreq: 'monthly', priority: '0.4' },
  '/privacy': { index: true, changefreq: 'monthly', priority: '0.4' },
  '/accessibility': { index: true, changefreq: 'monthly', priority: '0.4' },
  '/terms': { index: true, changefreq: 'monthly', priority: '0.3' },
  '/developers': { index: true, changefreq: 'monthly', priority: '0.4' },
  '/connect': { index: true, changefreq: 'monthly', priority: '0.7' },

  '/settings': {
    index: false,
    because: 'this browser’s own preferences; there is nothing here to search for',
  },
  '/headset': {
    index: false,
    because: 'a hand-off page whose content is a QR code for the address the player arrived from',
  },
  '/leaderboards': {
    index: false,
    because: '`leaderboards.get` is auth: user, so a crawler reaches a sign-in prompt',
  },
  '/leaderboards/:machineId': {
    index: false,
    because: 'the same board, named by machine, behind the same account requirement',
  },
  '/tournaments': {
    index: false,
    because: '`tournaments.current` is auth: user, so a crawler reaches a sign-in prompt',
  },
  '/account': { index: false, because: 'behind the account boundary' },
  '/replays/:replayId': {
    index: false,
    because: 'one player’s game, by opaque id — not enumerable and not a destination',
  },
  '/link-headset': {
    index: false,
    because:
      'a security prompt, arrived at by being told to by a headset; routes.ts already argues it ' +
      'should never be one tap from idle browsing',
  },
  '/moderation': {
    index: false,
    because:
      'the review queue; `moderation.queue` answers forbidden to everybody who is not a ' +
      'moderator, and an indexed address for it would be an invitation to try',
  },
  '/friends': { index: false, because: 'behind the account boundary' },
  '/history': {
    index: false,
    because: 'this browser’s own stores; a stranger’s is empty',
  },
  '/profile/:handle': {
    index: false,
    because: 'a page about one person; a sitemap of handles is a directory nobody asked to publish',
  },
  '/builder/:machineId': {
    index: false,
    because: 'an author’s workspace, by id',
  },
}

/**
 * Addresses that are real files rather than routes, and are not pages a player is looking for.
 *
 * `/harness.html` is the second entry point in `vite.config.ts` and Render serves it as a file
 * before it applies the SPA rewrite, so it is genuinely reachable and genuinely indexable. The
 * 2-D collision view is for settling "is the model wrong or is the physics wrong", which is not a
 * question anybody arrives from a search engine with.
 */
const NOT_A_PAGE: readonly string[] = ['/harness.html']

/** One entry of the sitemap: an absolute address and what the protocol wants said about it. */
export interface SitemapUrl {
  /** The route this address is, e.g. `/play/neon-nova` — what a `lastmod` is keyed on. */
  readonly path: string
  readonly loc: string
  readonly changefreq: ChangeFrequency
  readonly priority: string
}

/** A machine id, as it appears in a URL. Anything else is refused rather than escaped. */
const MACHINE_ID = /^[a-z0-9][a-z0-9-]*$/u

/**
 * Every address the sitemap lists, in the route table's own order.
 *
 * `:machineId` is expanded over the roster the caller supplies — the build passes the directory
 * names under `machines/`, which is the first-party roster this build ships. Any **other**
 * parameter in an indexable route throws: a `{loc}` containing a colon is a URL that 404s, and the
 * failure it produces is a search engine indexing a not-found page rather than a build error.
 */
export function indexableUrls(machineIds: readonly string[]): readonly SitemapUrl[] {
  for (const id of machineIds) {
    if (!MACHINE_ID.test(id)) throw new Error(`sitemap: not a machine id: ${id}`)
  }

  const urls: SitemapUrl[] = []
  for (const [pattern, decision] of Object.entries(INDEXING)) {
    if (!decision.index) continue

    const { changefreq, priority } = decision
    const paths = pattern.includes(':machineId')
      ? machineIds.map((id) => pattern.replace(':machineId', id))
      : [pattern]

    for (const path of paths) {
      if (path.includes(':')) {
        throw new Error(`sitemap: ${pattern} is indexable and carries a parameter nothing expands`)
      }
      urls.push({ path, loc: `${SITE_ORIGIN}${path}`, changefreq, priority })
    }
  }
  return urls
}

/**
 * Refused routes the shell links, which must be refused with `noindex` rather than `Disallow`
 * (S5-07).
 *
 * **A `Disallow` on a linked page is worse than no rule at all.** It stops the fetch, so Google
 * never reads the page and never sees a `noindex` or a canonical — and a URL it keeps finding
 * links to but may not read is one it can list on the strength of the links alone, with no title
 * and no snippet. That is the *"Indexed, though blocked by robots.txt"* warning, and S5-06 created
 * the conditions for it the moment the chrome went into the prerendered HTML: `/settings`,
 * `/headset` and `/leaderboards` went from being linked in JavaScript to being linked from nine
 * to twelve documents.
 *
 * These stay crawlable and carry `X-Robots-Tag: noindex` from `render.yaml`.
 * `test/seo/crawlDirectives.test.ts` holds the two files together in both directions.
 *
 * Only whole paths, never a parameterised prefix: a header rule in `render.yaml` matches an
 * address, and `/leaderboards/:machineId` is not one the chrome links anyway.
 */
export function noindexPaths(): readonly string[] {
  return Object.entries(INDEXING)
    .filter(([pattern, decision]) => !decision.index && CHROME_LINKED_PATHS.includes(pattern))
    .map(([pattern]) => pattern)
}

/**
 * Paths a crawler is asked to leave alone: the prefix of every route that is not indexed **and
 * that nothing links**.
 *
 * The exclusion is `noindexPaths` above, and dropping a parent can leave its child behind on its
 * own prefix — `/leaderboards` goes and `/leaderboards/` stays, which is exactly right: the board
 * is linked and a board by machine is not.
 */
export function disallowedPaths(): readonly string[] {
  const linked = new Set(noindexPaths())
  const paths = Object.entries(INDEXING)
    .filter(([pattern, decision]) => !decision.index && !linked.has(pattern))
    // Up to the first parameter, which is what a `Disallow` prefix can express. `/builder/:machineId`
    // becomes `/builder/`, and the trailing slash is load-bearing: prefix matching means `/builder/`
    // stops `/builder/anything` and leaves `/builder` itself alone, which is exactly the split.
    .map(([pattern]) => {
      const cut = pattern.indexOf('/:')
      return cut === -1 ? pattern : `${pattern.slice(0, cut)}/`
    })
  const all = [...new Set([...paths, ...NOT_A_PAGE])]
  // A prefix already covers everything under it, so `/leaderboards` and `/leaderboards/` are one
  // rule written twice. Dropped rather than left in: a `robots.txt` with a redundant line is a
  // `robots.txt` somebody has to work out the redundancy of before they can change it safely.
  return all.filter((path) => !all.some((other) => other !== path && path.startsWith(other)))
}

/**
 * `robots.txt`.
 *
 * `Allow: /` first and the refusals after it, because everything this product serves is public
 * unless it is one of the three kinds listed at the top of this file. The `Sitemap:` line is
 * absolute, which the protocol requires.
 */
export function renderRobots(): string {
  const lines = [
    '# © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com',
    '# Generated by the build from src/seo/sitemap.ts. Do not edit; edit the module.',
    '',
    'User-agent: *',
    'Allow: /',
    ...disallowedPaths().map((path) => `Disallow: ${path}`),
    '',
    `Sitemap: ${SITE_ORIGIN}/sitemap.xml`,
    '',
  ]
  return lines.join('\n')
}

/** `&`, `<` and `>` in a URL this product generated would be a defect; escaped rather than trusted. */
const escapeXml = (text: string): string =>
  text.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;')

/**
 * `sitemap.xml`.
 *
 * `lastmod` is the build's own date, passed in rather than read from a clock here: this module is
 * pure, and a test that asserts a document containing `new Date()` asserts nothing on the day it
 * is run.
 */
/**
 * When each page last changed, keyed by route (S7-04).
 *
 * **A date per page, and no date at all where the build cannot know one.** Every URL carrying the
 * build's own date — which is what this wrote until S7-04 — told Google that all twelve pages
 * changed on every deploy, including the deploys that changed no copy whatsoever. A `lastmod` a
 * crawler learns to distrust is worse than none: the protocol has no way to say "this one I mean",
 * so the whole signal goes.
 *
 * The build fills this from the last commit that touched what each page is made of. Where it
 * cannot find out — a shallow clone, or no git — the entry is absent and the element is omitted,
 * which is the one honest answer available and is what Google asks for.
 */
export type LastModified = Readonly<Record<string, string>>

export function renderSitemap(machineIds: readonly string[], lastmod: LastModified): string {
  for (const [path, date] of Object.entries(lastmod)) {
    if (!/^\d{4}-\d{2}-\d{2}$/u.test(date)) {
      throw new Error(`sitemap: lastmod for ${path} is not a W3C date: ${date}`)
    }
  }

  const entries = indexableUrls(machineIds).map((url) =>
    [
      '  <url>',
      `    <loc>${escapeXml(url.loc)}</loc>`,
      // Omitted rather than guessed. See `LastModified`.
      ...(lastmod[url.path] === undefined ? [] : [`    <lastmod>${lastmod[url.path]}</lastmod>`]),
      `    <changefreq>${url.changefreq}</changefreq>`,
      `    <priority>${url.priority}</priority>`,
      '  </url>',
    ].join('\n'),
  )

  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    /*
      **The em dash is load-bearing, and this cost a submission.** The project's copyright line is
      written `All rights reserved. -- www.holmesconsultants.com`, and XML forbids `--` *anywhere
      inside a comment*. HTML parsers tolerate it, which is why `index.html` has carried the same
      string for months; a sitemap is read by an XML parser, and Google answered the first
      submission with **"Sitemap can be read, but has errors — Parsing error, 1 instance"** and
      **0 discovered pages**. Measured on the live site on 2026-09-03.

      So the one file in this product whose reader is a strict parser writes the line with an em
      dash. Everything else about it is the same, and `test/seo/sitemap.test.ts` now refuses a `--`
      anywhere in the document.
    */
    '<!-- © 2026 Holmes Computer Consultants. All rights reserved. — www.holmesconsultants.com -->',
    '<!-- Generated by the build from src/seo/sitemap.ts. Do not edit; edit the module. -->',
    '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">',
    ...entries,
    '</urlset>',
    '',
  ].join('\n')
}
